// ── liveDisplayState — one canonical answer to "is this game live?" ──
//
// #121. Card surfaces used to read `GameRow.gameState === 'live'` for their
// LIVE treatment. That field is marketStore's `deriveGameState` heuristic:
// "a venue book is tradable AND Date.now() has passed the scheduled start".
// It is a reasonable guess and a poor claim — a postponed game keeps its
// tradable book past its stale start time and reads as LIVE indefinitely,
// and a finished game stays LIVE until a venue closes the book or a
// hardcoded per-sport duration timer expires (MLB: 4h + 30m grace).
//
// A canonical `statusId` is the authoritative signal (1 = scheduled,
// 2 = live, 3 = final). This helper takes one, plus the row's existing
// heuristic, and returns the ONE state every surface renders from. Nobody
// re-derives it.
//
// ── The canonical state is supplied, never inferred here ─────────────
// Focused GameDetail rows receive the per-game odds-stream state. #163 also
// permits the dashboard's existing batched game-state snapshot to feed its
// left-side chip while that poll is current, keeping the chip consistent with
// the score/period already rendered beside it. Hydra #19 remains the longer-
// term source improvement. A missing or stale source resolves to `unknown`,
// never a clock-derived LIVE claim.
//
// It deliberately takes NO clock. The time arithmetic already happened
// upstream in `deriveGameState`; redoing it here would be a second,
// drifting source of truth, and would put a fresh `Date.now()` inference
// on exactly the surfaces this issue asks to keep one off.
//
// ── The fourth state ────────────────────────────────────────────────
// When no trustworthy canonical state exists — which is every game until
// #19, and after it any sport the source doesn't cover — the
// honest answer is "the clock says this started and nothing can confirm
// it" — not LIVE, but not silence either. Silence renders identically to
// pregame, which is the failure that opened this issue.
//
// That follows the convention this repo already uses for absent data:
// #106 ("unknown timestamps render as a dash; the client does not infer or
// fabricate them" — see `cardFormat.ts` `fmtLastFillAt`) and
// `components/atoms/OrderbookFreshness.tsx`, which gives a missing frame a
// neutral marker plus an explaining tooltip, and reserves distinct colored
// treatment for states it can actually confirm.

import { isConfirmedFinal } from './periodLabel';

/** marketStore's per-row heuristic (`GameRow.gameState`). */
export type RowGameState = 'live' | 'pre' | 'final';

/**
 * How long the gameStates poll may be failing before its cached snapshot
 * stops counting as current.
 *
 * `gameStatesStore` never clears `byGameKey` on a failed poll — it only
 * records the error and keeps the last good snapshot. That is the right
 * call for scores (a number a few seconds old still beats a blank), but
 * it means a frozen feed would otherwise assert LIVE forever: the feed
 * dies at 7pm mid-game and the card still claims live at midnight.
 *
 * The poll runs every 5s, so this is 12 consecutive misses — far past any
 * transient blip. Matches `useHydra`'s WATCHDOG_STALE_MS rather than
 * introducing a second number for the same idea.
 */
export const FEED_STALE_MS = 60_000;

/**
 * True when a canonical snapshot can no longer be treated as current.
 *
 * Requires BOTH an error and an aged-out snapshot, deliberately:
 *
 *  • Error alone would flicker. One transient failure would drop every
 *    card to `unknown` for a poll cycle.
 *  • Age alone would misfire on a backgrounded tab. `useGameStatesPoll`
 *    pauses on `document.hidden`, so `lastFetchMs` freezes while the tab
 *    is away with nothing actually wrong; it refetches immediately on
 *    return. Age-only would flash every card on every tab switch.
 *
 * A never-successful poll (`lastFetchMs === 0`) reads as maximally aged,
 * which is correct — there is no snapshot to trust.
 *
 * The dashboard's #163 consistency path uses this gate before trusting its
 * retained batched snapshot for the left-side LIVE chip.
 */
export function isGameStateFeedStale(
  error: string | null,
  lastFetchMs: number,
  nowMs: number,
): boolean {
  if (error == null) return false;
  return nowMs - lastFetchMs > FEED_STALE_MS;
}

/**
 * What a card should render.
 *
 *   live         Unabated confirms the game is in progress.
 *   final        The game is over — confirmed final, or the row's markets
 *                are terminal.
 *   unknown      The clock says it started; nothing can confirm it.
 *                Never claims LIVE.
 *   pre          Not started.
 */
export type LiveDisplayState = 'live' | 'final' | 'unknown' | 'pre';

/** Structural shape shared by `UnabatedGameState` and `OddsStreamGameState`
 *  — taken by shape so this module depends on neither service. */
export interface CanonicalGameStateInput {
  statusId?: number | null;
  awayScore?: unknown;
  homeScore?: unknown;
}

/**
 * The canonical record if it can be believed, otherwise null.
 *
 * A `statusId === 3` carrying no score is a stale/garbage record, not a
 * final: on 2026-06-12 a May-20 soccer record reported a game that was in
 * play as final with null scores, which blanked the Odds column and
 * archived the game page. Such a record must not be treated as authority
 * for ANY state — drop it and let the caller fall back.
 *
 * This is an allow-list rather than a deny-list so that a statusId we
 * don't recognize (hydra adding a code) degrades the same safe way instead
 * of being silently read as "not live".
 *
 * Exported for `eventStateCertainty` (#481), which needs the same verdict
 * on the same record. Two allow-lists would drift, and the one that drifted
 * would be the one deciding whether to warn a trader.
 */
export function trustedCanonicalState(
  gs: CanonicalGameStateInput | null | undefined,
): CanonicalGameStateInput | null {
  if (gs == null) return null;
  if (gs.statusId === 1 || gs.statusId === 2) return gs;
  if (isConfirmedFinal(gs)) return gs;
  return null;
}

/**
 * Resolve the single display state for a game card.
 *
 * Precedence, and why:
 *
 *  1. Canonical LIVE wins outright. It outranks a `final` row state on
 *     purpose — `deriveGameState` coerces to final once a hardcoded sport
 *     duration + grace elapses, so a long extra-innings game reads final
 *     while Unabated still has it in play. The feed is right.
 *  2. Canonical CONFIRMED FINAL. This is the lag fix: LIVE now clears
 *     within one poll of the feed reporting final, instead of waiting on a
 *     venue to close the book or that duration timer to run out.
 *  3. The row's own `final`, left exactly as it behaves today. This issue
 *     is scoped to the LIVE indicator; re-sourcing FINAL would also change
 *     row treatment (price pills, dimming, the winner check) well past it.
 *  4. Canonical SCHEDULED. Unabated covers this game and says it has not
 *     started, so it has not — whether that is a postponement or a feed a
 *     few seconds behind first pitch, which self-corrects on the next
 *     poll. Either way it is not LIVE.
 *  5. Nothing trustworthy, and the heuristic says live → `unknown`.
 *  6. Otherwise pregame.
 */
export function resolveLiveDisplayState(
  canonical: CanonicalGameStateInput | null | undefined,
  rowGameState: RowGameState,
): LiveDisplayState {
  const trusted = trustedCanonicalState(canonical);

  if (trusted?.statusId === 2) return 'live';
  if (isConfirmedFinal(trusted)) return 'final';
  if (rowGameState === 'final') return 'final';
  if (trusted != null) return 'pre'; // statusId 1 — postponed, or poll lag
  if (rowGameState === 'live') return 'unknown';
  return 'pre';
}
